import type {
  Flow_AbstractComponent,
  Flow_Diff,
  Flow_ComponentProps,
  Flow_ObjMap,
} from 'src/types/flow-to-typescript-codemod';
import * as React from 'react';

import logger from 'src/utils/logger';

import * as api from 'src/utils/api-no-store';
import {createElement} from 'src/utils/dom';

import css from './beefree-editor.css';
import classify from 'src/utils/classify';


let editorLoaded = false;

// These are promises returned when invoking custom ui for beefree.
type DialogObjectPromise<T> = {
  label: string;
  handler: (resolve: (arg1: T) => unknown, reject: () => unknown) => unknown;
};

type SpecialLink = {
  type: string; // Used for grouping similar links together;
  label: string;
  link: string;
};

// The name is the friendly string displayed in the ui and
// the value is the raw value inserted in the content
type NameValue = {
  name: string;
  value: string;
};

// see https://tools.ietf.org/html/rfc6902
type JSONPatchOperation =
  | {
      op: 'add' | 'remove' | 'replace' | 'test';
      path: string; // looks like '/key/subkey/subsubkey',;
      value:
        | string
        | number
        | Record<any, any>
        | boolean
        | null
        | Array<string | number | Record<any, any> | boolean | null>;
    }
  | {
      op: 'move' | 'copy';
      path: string;
      from: string;
      value:
        | string
        | number
        | Record<any, any>
        | boolean
        | null
        | Array<string | number | Record<any, any> | boolean | null>;
    };

// see https://docs.beefree.io/tracking-message-changes/#callback-parameters
export type OnChangeResponse = {
  code: string; // actually an enum like "01" | "02" | ...;
  description: string;
  value: string;
  patches: JSONPatchOperation[];
};

// TODO(marcos): figure out what this is so we can port it to unlayer
export type BeefreeDocument = Record<any, any>;

// Many of these props are defined by the beefree editor here:
//   https://docs.beefree.io/configuration-parameters/
type BeeFreeProps = {
  initialTemplate: BeefreeDocument; // this is a json beefree doc to initialize the;
  // editor with (probably from a prior save)

  uid: string; // This value should probably be the user id of the current;
  // person using the webapp, it is used to track the number
  // of billable users for befree and it prevents users from
  // seeing each other's content
  container?: string; // this is the name of a unique dom element that;
  // beefree can take over.
  autosave?: number; // default: false;
  language?: string; // default 'en-US';
  trackChanges?: boolean; // default: false;
  specialLinks?: SpecialLink[]; // default: [];
  mergeTags?: NameValue[]; // default: [];
  mergeContents?: NameValue[]; // default: [];
  preventClose?: boolean; // default false;
  editorFonts?: Record<any, any>;
  roleHash?: string; // default "";
  rowDisplayConditions?: Record<any, any>; // default {};
  // almost everywhere that bee emits json in a callback it really is
  // emitting a stringified json blob.
  onSave?: (jsonFile: string, htmlFile: string) => unknown;
  onChange?: (jsonFile: string, response: OnChangeResponse) => unknown;
  onSaveAsTemplate?: (jsonFile: string) => unknown;
  onAutoSave?: (jsonFile: string) => unknown;
  onSend?: (htmlFile: string) => unknown;
  onLoad?: (jsonFile: string) => unknown;
  onError?: (errorMessage: string) => unknown;
  onWarning?: (alertMessage: string) => unknown;
  onTogglePreview?: (isPreviewing: boolean) => unknown;
  contentDialog?: {
    specialLinks?: DialogObjectPromise<SpecialLink>;
    mergeTags?: DialogObjectPromise<NameValue>;
    mergeContents?: DialogObjectPromise<NameValue>;
    rowDisplayConditions?: DialogObjectPromise<{
      type: string;
      label: string;
      description: string;
      before: string;
      after: string;
    }>;
    addOn?: DialogObjectPromise<unknown>;
    externalContentURLs?: DialogObjectPromise<NameValue>;
  };
  translations?: {
    [editorComponentName: string]: {
      [labelIdentifier: string]: string;
    };
  };
  containerClassName?: string;
  customCss?: string;
};

// This is the type of the imperativeHandle attached by useImperativeHandle
type BeeFreeEditorRef = {
  handleSave: () => unknown;
  togglePreview: () => unknown;
  toggleStructure: () => unknown;
  handleSaveTemplate: () => unknown;
  handleLoad: (doc?: BeefreeDocument) => unknown;
};

const BeefreeEditor = (
  {uid, initialTemplate, onSave, containerClassName, ...props}: BeeFreeProps,
  ref:
    | ((arg1: null | BeeFreeEditorRef) => unknown)
    | {
        current: null | BeeFreeEditorRef;
      },
) => {
  const [id, setId] = React.useState<any>(null);
  const [beefreeScriptLoaded, setBeefreeScriptLoaded] = React.useState(false);
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const beeFreeInstanceRef = React.useRef();

  // @ts-expect-error - TS2345 - Argument of type '{ current: BeeFreeEditorRef | null; } | ((arg1: BeeFreeEditorRef | null) => unknown)' is not assignable to parameter of type 'Ref<BeeFreeEditorRef> | undefined'.
  React.useImperativeHandle(ref, () => ({
    handleSave: () => {
      // This is not used right now, but it's present in case we
      // decide to support this sort of interaction.
      // $FlowOptionalCall
      // @ts-expect-error - TS2339 - Property 'save' does not exist on type '{}'.
      beeFreeInstanceRef.current?.save();
    },
    togglePreview: () => {
      // $FlowOptionalCall
      // @ts-expect-error - TS2339 - Property 'togglePreview' does not exist on type '{}'.
      beeFreeInstanceRef.current?.togglePreview();
    },
    toggleStructure: () => {
      // $FlowOptionalCall
      // @ts-expect-error - TS2339 - Property 'toggleStructure' does not exist on type '{}'.
      beeFreeInstanceRef.current?.toggleStructure();
    },
    // this method flushes the current json state of the beefree editor
    // into the onSaveAsTemplate
    handleSaveTemplate: () => {
      // @ts-expect-error - TS2339 - Property 'saveAsTemplate' does not exist on type '{}'.
      beeFreeInstanceRef.current?.saveAsTemplate();
    },

    // this causes the editor to load this new doc (not a string, an object)
    handleLoad: (doc?: BeefreeDocument) => {
      // @ts-expect-error - TS2339 - Property 'load' does not exist on type '{}'.
      beeFreeInstanceRef.current?.load(doc);
    },
  }));

  React.useEffect(() => {
    if (!editorLoaded) {
      const script = createElement(document, 'script', {
        src: 'https://app-rsrc.getbee.io/plugin/BeePlugin.js',
      });
      script.onload = () => {
        setBeefreeScriptLoaded(true);
      };
      // $FlowOptionalCall
      document.body?.appendChild(script);
      editorLoaded = true;
    } else {
      setBeefreeScriptLoaded(true);
    }
    const id = 'beefree-' + Math.round(Math.random() * 10000000).toString();
    setId(id);
  }, []);

  React.useEffect(() => {
    if (editorLoaded && beefreeScriptLoaded) {
      api.post('agency/beefree-plugin-token').then(
        (response) => {
          if (id) {
            // @ts-expect-error - TS2551 - Property 'BeePlugin' does not exist on type 'Window & typeof globalThis'. Did you mean 'Plugin'?
            window.BeePlugin.create(
              response,
              {
                uid,
                container: id,
                onSave,
                ...props,
              },
              // @ts-expect-error - TS7006 - Parameter 'instance' implicitly has an 'any' type.
              (instance) => {
                // once the editor has been created, beeFreeInstanceRef is the
                // forwarded ref for this component
                beeFreeInstanceRef.current = instance;

                // load the editor with the template passed in via props
                instance.start(initialTemplate);
              },
            );
          }
        },
        (error) => {
          logger.error(error);
          logger.error('beefree token fetching threw an error');
        },
      );
    }
  }, [onSave, beefreeScriptLoaded]);

  return (
    <div
      id={id}
      className={classify(css.editorContainer, containerClassName)}
    />
  );
};

// @ts-expect-error - TS2352 - Conversion of type 'ForwardRefExoticComponent<BeeFreeEditorRef & RefAttributes<BeeFreeProps>>' to type 'AbstractComponent<BeeFreeProps, BeeFreeEditorRef>' may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to 'unknown' first.
export default React.forwardRef<BeeFreeProps, BeeFreeEditorRef>(
  // @ts-expect-error - TS2345 - Argument of type '({ uid, initialTemplate, onSave, containerClassName, ...props }: BeeFreeProps, ref: ((arg1: null | BeeFreeEditorRef) => unknown) | { current: null | BeeFreeEditorRef; }) => React.JSX.Element' is not assignable to parameter of type 'ForwardRefRenderFunction<BeeFreeProps, BeeFreeEditorRef>'.
  BeefreeEditor,
) as Flow_AbstractComponent<BeeFreeProps, BeeFreeEditorRef>;
